import './styles.scss'
import image from '../../../Assets/Images/quote-modal-img.jpeg'
import { CSSTransition } from 'react-transition-group'
import { useEffect, useRef, useState } from 'react'
import { useForm } from '../../../Hooks/useForm'
import emailjs from '@emailjs/browser'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { SERVICES } from '../../../constants'

const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID
const templateId = process.env.REACT_APP_EMAILJS_QUOTE_TEMPLATE_ID
const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY

const REQUIRED = ['name', 'email', 'service', 'origin', 'destination', 'cargo']

const validate = (name, value) => {
  switch (name) {
    case 'name':
      return value.trim() ? '' : 'Full name is required.'
    case 'email':
      if (!value.trim()) return 'Email address is required.'
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
        ? ''
        : 'Please enter a valid email address.'
    case 'service':
      return value ? '' : 'Please select a service.'
    case 'origin':
      return value.trim() ? '' : 'Origin is required.'
    case 'destination':
      return value.trim() ? '' : 'Destination is required.'
    case 'cargo':
      return value.trim() ? '' : 'Cargo description is required.'
    default:
      return ''
  }
}

const QuoteForm = ({ showForm, handleClose }) => {
  const formRef = useRef(null)
  const [honeypot, setHoneypot] = useState('')

  const { form, reset, onChange } = useForm({
    name: '',
    email: '',
    phone: '',
    service: '',
    origin: '',
    destination: '',
    cargo: '',
    weight: '',
    msg: '',
    loading: false,
  })

  const [touched, setTouched] = useState({})
  const [fieldErrors, setFieldErrors] = useState({})
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    return reset
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChange = (e) => {
    onChange(e.target.name, e.target.value)
    if (touched[e.target.name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [e.target.name]: validate(e.target.name, e.target.value),
      }))
    }
  }

  const handleBlur = (e) => {
    const { name, value } = e.target
    setTouched((prev) => ({ ...prev, [name]: true }))
    setFieldErrors((prev) => ({ ...prev, [name]: validate(name, value) }))
  }

  const fieldState = (name) => {
    if (!touched[name]) return ''
    return fieldErrors[name] ? 'error' : 'valid'
  }

  const handleClick = (e) => e?.stopPropagation()

  const onClose = () => {
    handleClose()
    setSuccess(false)
    setTouched({})
    setFieldErrors({})
    reset()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const allTouched = REQUIRED.reduce((acc, k) => ({ ...acc, [k]: true }), {})
    const errors = REQUIRED.reduce(
      (acc, k) => ({ ...acc, [k]: validate(k, form[k]) }),
      {}
    )

    setTouched((prev) => ({ ...prev, ...allTouched }))
    setFieldErrors((prev) => ({ ...prev, ...errors }))

    if (REQUIRED.some((k) => errors[k])) return

    // honeypot check — bots fill this, humans don't
    if (honeypot) return

    onChange('loading', true)

    try {
      await emailjs.send(
        serviceId,
        templateId,
        {
          name: form.name,
          email: form.email,
          phone: form.phone,
          service: form.service,
          origin: form.origin,
          destination: form.destination,
          cargo: form.cargo,
          weight: form.weight,
          msg: form.msg,
        },
        publicKey
      )
      reset()
      setTouched({})
      setFieldErrors({})
      setSuccess(true)
    } catch {
      setFieldErrors((prev) => ({
        ...prev,
        _submit: 'Something went wrong. Please try again.',
      }))
    }

    onChange('loading', false)
  }

  const Field = ({ id, name, label, required, error, children }) => (
    <div className={`quote-form__field quote-form__field--${fieldState(name)}`}>
      <label htmlFor={id}>
        {label}{required && <span className="req"> *</span>}
      </label>
      {children}
      {touched[name] && error && (
        <span className="quote-form__field__error">{error}</span>
      )}
    </div>
  )

  return (
    <CSSTransition
      in={showForm}
      nodeRef={formRef}
      timeout={300}
      unmountOnExit
      classNames="quote-form"
    >
      <div ref={formRef} className="quote-form" onClick={handleClick}>
        <div className="quote-form__image">
          <div className="quote-form__image__overlay">
            <h2>Get A Free Quote</h2>
            <p>Fast, reliable logistics tailored to your needs.</p>
          </div>
          <img src={image} alt="" />
        </div>

        <div className="quote-form__content">
          <div className="quote-form__content__header">
            <h5>Request a Quote</h5>
            <button
              type="button"
              className="quote-form__close"
              onClick={onClose}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={solid('xmark')} />
            </button>
          </div>

          {success ? (
            <div className="quote-form__success">
              <div className="quote-form__success__icon">
                <FontAwesomeIcon icon={solid('circle-check')} />
              </div>
              <h4>Quote Request Sent!</h4>
              <p>Thank you! We'll get back to you with a quote shortly.</p>
              <button className="quote-form__success__btn" onClick={onClose}>
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {/* Honeypot — hidden from real users, bots fill it */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                style={{ display: 'none' }}
                tabIndex={-1}
                autoComplete="off"
              />

              <span className="quote-form__required-note">
                <span className="req">*</span> Required fields
              </span>

              {fieldErrors._submit && (
                <div className="quote-form__alert">
                  <FontAwesomeIcon icon={solid('circle-exclamation')} />
                  {fieldErrors._submit}
                </div>
              )}

              <div className="quote-form__section-label">Contact Details</div>

              <div className="quote-form__row">
                <Field id="qf-name" name="name" label="Full Name" required error={fieldErrors.name}>
                  <input
                    id="qf-name"
                    name="name"
                    type="text"
                    placeholder="John Smith"
                    value={form.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Field>

                <Field id="qf-email" name="email" label="Email" required error={fieldErrors.email}>
                  <input
                    id="qf-email"
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    value={form.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Field>
              </div>

              <div className="quote-form__row">
                <Field id="qf-phone" name="phone" label="Phone Number" error={fieldErrors.phone}>
                  <input
                    id="qf-phone"
                    name="phone"
                    type="tel"
                    placeholder="+44 000 000 0000"
                    value={form.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Field>

                <Field id="qf-service" name="service" label="Service" required error={fieldErrors.service}>
                  <select
                    id="qf-service"
                    name="service"
                    value={form.service}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="" disabled>Select a service...</option>
                    {SERVICES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="quote-form__section-label">Shipment Details</div>

              <div className="quote-form__row">
                <Field id="qf-origin" name="origin" label="Origin" required error={fieldErrors.origin}>
                  <input
                    id="qf-origin"
                    name="origin"
                    type="text"
                    placeholder="City or country"
                    value={form.origin}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Field>

                <Field id="qf-destination" name="destination" label="Destination" required error={fieldErrors.destination}>
                  <input
                    id="qf-destination"
                    name="destination"
                    type="text"
                    placeholder="City or country"
                    value={form.destination}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Field>
              </div>

              <div className="quote-form__row">
                <Field id="qf-cargo" name="cargo" label="Cargo Description" required error={fieldErrors.cargo}>
                  <input
                    id="qf-cargo"
                    name="cargo"
                    type="text"
                    placeholder="e.g. Electronics, Furniture"
                    value={form.cargo}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Field>

                <Field id="qf-weight" name="weight" label="Approx. Weight / Size" error={fieldErrors.weight}>
                  <input
                    id="qf-weight"
                    name="weight"
                    type="text"
                    placeholder="e.g. 200kg, 2 pallets"
                    value={form.weight}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Field>
              </div>

              <Field id="qf-msg" name="msg" label="Additional Notes" error={fieldErrors.msg}>
                <textarea
                  id="qf-msg"
                  name="msg"
                  rows={3}
                  placeholder="Any special requirements or extra details..."
                  value={form.msg}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </Field>

              <button
                type="submit"
                className="quote-form__submit"
                disabled={form.loading}
              >
                {form.loading ? (
                  <>
                    <FontAwesomeIcon icon={solid('circle-notch')} spin />
                    Sending...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={solid('paper-plane')} />
                    Send Request
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </CSSTransition>
  )
}

export default QuoteForm
